import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

/**
 * Minimal Premium Navigation
 * Top-right subtle menu control with glass treatment
 */
export default function MinimalNavigation() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const menuItems = [
    { label: 'OUR WORK', href: '#work' },
    { label: 'SYSTEMS', href: '#systems' },
    { label: 'ABOUT', href: '#about' },
    { label: 'BUILD WITH ETERNITY', href: '#contact' },
    { label: 'TEAM PORTAL', href: '/login', isSpecial: true }
  ];

  return (
    <>
      {/* Menu Button - Top Right */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-6 right-6 z-50 w-11 h-11 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/[0.06] rounded-xl transition-all duration-300 backdrop-blur-sm"
        aria-label="Menu"
      >
        <motion.div
          animate={isOpen ? "open" : "closed"}
          className="w-5 h-4 flex flex-col justify-between"
        >
          <motion.span
            variants={{
              closed: { rotate: 0, y: 0 },
              open: { rotate: 45, y: 8 }
            }}
            className="w-full h-0.5 bg-current rounded-full origin-center transition-all"
          />
          <motion.span
            variants={{
              closed: { opacity: 1 },
              open: { opacity: 0 }
            }}
            className="w-full h-0.5 bg-current rounded-full transition-all"
          />
          <motion.span
            variants={{
              closed: { rotate: 0, y: 0 },
              open: { rotate: -45, y: -8 }
            }}
            className="w-full h-0.5 bg-current rounded-full origin-center transition-all"
          />
        </motion.div>
      </button>

      {/* Full Screen Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#0A0A0F]/97 backdrop-blur-3xl"
            onClick={() => setIsOpen(false)}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.nav
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="flex flex-col items-center gap-8"
                onClick={(e) => e.stopPropagation()}
              >
                {menuItems.map((item, index) => (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    onClick={(e) => {
                      if (item.href.startsWith('/')) {
                        e.preventDefault();
                        navigate(item.href);
                        setIsOpen(false);
                      } else {
                        setIsOpen(false);
                      }
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                    className={`group relative text-2xl md:text-3xl font-semibold tracking-tight uppercase transition-all duration-300 ${
                      item.isSpecial
                        ? 'text-[#FF6B35] hover:text-[#FF8F66]'
                        : 'text-white/70 hover:text-white'
                    }`}
                    style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}
                  >
                    {item.label}
                    
                    {/* Subtle underline on hover */}
                    <motion.span
                      className={`absolute -bottom-1 left-0 right-0 h-[2px] ${
                        item.isSpecial ? 'bg-[#FF6B35]' : 'bg-white'
                      } origin-left`}
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.a>
                ))}
              </motion.nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
